import { type QueryClient } from '@tanstack/react-query';

import {
  type AvatarData,
  getGetMyProfileQueryKey,
  type SuccessBodyMyProfileRes,
} from '@/api';

type MyProfileQueryPatch = {
  avatarData?: AvatarData;
  username?: string;
};

export function normalizeQuizAvatarData(avatarData: AvatarData): AvatarData {
  const normalizePart = (value: number | undefined) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
    return Math.abs(Math.floor(value));
  };

  // 프로필 API 는 타입(optional)과 달리 accessoryIds: null 을 내려준다.
  // `!= null` 로 받아야 Object.entries(null) 로 터지지 않는다 (QUIZ-APP-7Q5).
  const accessoryIds =
    avatarData.accessoryIds != null
      ? Object.fromEntries(
          Object.entries(avatarData.accessoryIds)
            .filter((entry): entry is [string, number] => {
              const id = entry[1];
              return typeof id === 'number' && Number.isFinite(id);
            })
            .map(([slot, id]) => [slot, Math.abs(Math.floor(id))] as const),
        )
      : undefined;

  return {
    ...(normalizePart(avatarData.skinId) !== undefined && {
      skinId: normalizePart(avatarData.skinId),
    }),
    ...(normalizePart(avatarData.faceId) !== undefined && {
      faceId: normalizePart(avatarData.faceId),
    }),
    ...(normalizePart(avatarData.hairId) !== undefined && {
      hairId: normalizePart(avatarData.hairId),
    }),
    ...(normalizePart(avatarData.clothesId) !== undefined && {
      clothesId: normalizePart(avatarData.clothesId),
    }),
    ...(accessoryIds !== undefined && { accessoryIds }),
  };
}

export function syncMyProfileQueryCache(
  queryClient: QueryClient,
  patch: MyProfileQueryPatch,
) {
  if (patch.avatarData === undefined && patch.username === undefined) return;

  const avatarData =
    patch.avatarData !== undefined
      ? normalizeQuizAvatarData(patch.avatarData)
      : undefined;

  queryClient.setQueryData(
    getGetMyProfileQueryKey(),
    (old: SuccessBodyMyProfileRes | undefined) => {
      if (!old) return old;

      return {
        ...old,
        data: {
          ...old.data,
          ...(avatarData !== undefined && { avatarData }),
          ...(patch.username !== undefined && { username: patch.username }),
        },
      };
    },
  );

  void queryClient.invalidateQueries({
    queryKey: getGetMyProfileQueryKey(),
  });
}
